import {
  Language,
  Locale,
  PromotionResponseExtended,
  ProductExtended,
  SortIdOption,
  SortOrderOption,
  PromosInputParams,
  PromosOutputParams,
  PromosCMSParams,
  PromosOption,
  PromosSettings,
} from "@/../types/promos-types";
import { IdGenerator } from "vacv-common";
import type {
  Product,
  PromotionResponse,
} from "vacv-common/src/types/api/promotion/Api";
import { getEnvVar } from "@/utils";
import { yearMonthConverter } from "@/utils/dateUtils";
import qs from "qs";

const HOTEL_DETAILS_EN = getEnvVar("VITE_HOTEL_DETAILS_EN");
const HOTEL_DETAILS_FR = getEnvVar("VITE_HOTEL_DETAILS_FR");

const dollarFormatters = new Map();

function getFormatter(locale: string) {
  if (dollarFormatters.has(locale)) {
    return dollarFormatters.get(locale);
  } else {
    const f = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "CAD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    dollarFormatters.set(locale, f);
    return f;
  }
}

export function filterInvalidProducts(
  products: Array<ProductExtended>,
  promosParams: PromosOutputParams
) {
  return products.filter((product) => {
    return (
      hasValidPrice(product, promosParams) &&
      hasValidStarCategory(product) &&
      filteredByStarRatingSelected(product, promosParams)
    );
  });
}

function hasValidStarCategory(product: ProductExtended) {
  const starValue = Number(product.star_category);
  return !isNaN(starValue) && starValue > 0 && starValue <= 5;
}

function hasValidPrice(
  product: ProductExtended,
  promosParams: PromosOutputParams
) {
  if (!promosParams.priceCap) {
    return true;
  }
  const priceCap = parseInt(promosParams.priceCap);
  return product.price <= priceCap;
}

function filteredByStarRatingSelected(
  product: ProductExtended,
  promosParams: PromosOutputParams
) {
  if (!promosParams.starRating || !product.star_category) {
    return false;
  }
  return product.star_category >= promosParams.starRating;
}

export function formatPrice(value: number | undefined, locale: string): string {
  const formatter = getFormatter(locale);
  return formatter.format(value);
}

export function formatPromotion(
  p: Product,
  index: number,
  lang: Language,
  locale: Locale
) {
  const product: ProductExtended = {
    ...p,
    price_formatted: formatPrice(p.price, locale),
    hotel_details_link: getHotelDetailsLink(p.acv_hotel_id, p.hotel_name, lang),
    index: index,
    _id: IdGenerator.generateId(12),
  };
  return product;
}

export function formatPromosResponse(
  promotionsResponse: PromotionResponse,
  lang: Language
): PromotionResponseExtended {
  const promotionsResponseExtended =
    promotionsResponse as PromotionResponseExtended;
  if (promotionsResponse?.products?.length) {
    const locale: Locale = lang === "fr" ? "fr-CA" : "en-CA";
    promotionsResponseExtended.products = promotionsResponse.products.map(
      (promotion: Product, index: number) => {
        return formatPromotion(promotion, index, lang, locale);
      }
    );
  }
  return promotionsResponseExtended;
}

export function getHotelDetailsLink(
  hotel_code: string | undefined,
  hotel_name: string | undefined,
  lang: string
) {
  const url = lang === "fr" ? HOTEL_DETAILS_FR : HOTEL_DETAILS_EN;
  return `${url}/${hotel_code}/${hotel_name}?distribution=yes`;
}

// tripAdvisorErrorHandler is so PromosListItem.vue can hide tripadvisor iframes that are unsuccessful
export function tripAdvisorErrorHandler(e: MessageEvent) {
  if (e.origin && e.data) {
    if (e.origin.indexOf("tripadvisor.com") > -1) {
      if (e.data.error && e.data.frameName) {
        const iframe = document.getElementsByName(e.data.frameName)[0];
        if (iframe) {
          iframe.style.display = "none";
        }
      }
    }
  }
}

function sortByStarRatingAsc(a: ProductExtended, b: ProductExtended) {
  if (a.star_category && b.star_category) {
    if (a.star_category !== b.star_category) {
      return a.star_category > b.star_category ? 1 : -1;
    } else {
      return sortByPriceAsc(a, b);
    }
  }
  return 0;
}

function sortByStarRatingDesc(a: ProductExtended, b: ProductExtended) {
  if (a.star_category && b.star_category) {
    if (a.star_category !== b.star_category) {
      return a.star_category < b.star_category ? 1 : -1;
    } else {
      return sortByPriceAsc(a, b);
    }
  }
  return 0;
}

function sortByPriceAsc(a: ProductExtended, b: ProductExtended) {
  if (a.price && b.price) {
    return a.price > b.price ? 1 : -1;
  }
  return 0;
}

function sortByPriceDesc(a: ProductExtended, b: ProductExtended) {
  if (a.price && b.price) {
    return a.price > b.price ? -1 : 1;
  }
  return 0;
}

function sortByDateAsc(a: ProductExtended, b: ProductExtended) {
  if (a.departure_date && b.departure_date) {
    // date format is '2022-11-16' so we can use parseInt here
    const aDate = parseInt(a.departure_date.replaceAll("-", ""));
    const bDate = parseInt(b.departure_date.replaceAll("-", ""));
    return aDate > bDate ? 1 : -1;
  }
  return 0;
}

export function getSortFunction(id: SortIdOption, order: SortOrderOption) {
  switch (id) {
    case "star-rating":
      return order === "asc" ? sortByStarRatingAsc : sortByStarRatingDesc;
    case "price":
      return order === "asc" ? sortByPriceAsc : sortByPriceDesc;
    case "departure-date":
      return sortByDateAsc;
    default:
      return sortByPriceAsc;
  }
}

export const getPromosParamsFromURL = (
  searchQuery: string,
  dropdownsData: PromosCMSParams
) => {
  if (!searchQuery) {
    return {};
  }

  // INPUT QUERY
  // https://vacations.aircanada.com/en/all-deals/100003/last-minute-deals?tripMonth=09/2022&leaveFrom=YYZ&goTo=VRA&duration=8&vacationType=PACKAGE&promoStartDate=20240201&promoEndDate=20240331&promotionId=100003

  // OUTPUT QUERY
  // https://api-dev.vacv.com/promotion/promotions?gateway=YUL&destination=CUN&duration=7&year_month=2022-09&sort_by=departure_date&lang=en&item-per-page=200&page=1&promo_start_date=20240201&promo_end_date=20240331&promotion_id=100003

  const inputParamsQ: unknown = qs.parse(
    searchQuery.substring(searchQuery.indexOf("?") + 1)
  ); // convert string query to params object

  return getPromosParams(inputParamsQ as PromosInputParams, dropdownsData);
};

const getValidDropdownValue = (
  value: string,
  options?: PromosOption[]
): string | undefined => {
  return options?.some((month) => month.code === value) ? value : undefined;
};

const getValidDurationValue = (
  value: string,
  options?: string[]
): string | undefined => {
  return options?.includes(value) ? value : undefined;
};

const getPromosParams = (
  inputParams: PromosInputParams,
  dropdownsData: PromosCMSParams
): PromosSettings => {
  const promoSettings = {} as PromosSettings;

  const formattedYearMonth = yearMonthConverter(inputParams?.tripMonth);
  if (formattedYearMonth) {
    promoSettings.month = getValidDropdownValue(
      formattedYearMonth,
      dropdownsData.months
    );
  }
  if (inputParams.leaveFrom) {
    promoSettings.gateway = getValidDropdownValue(
      inputParams.leaveFrom,
      dropdownsData.gateways
    );
  }
  if (inputParams.goTo) {
    promoSettings.destination = getValidDropdownValue(
      inputParams.goTo,
      dropdownsData.destinations
    );
  }
  if (inputParams.duration) {
    promoSettings.duration = getValidDurationValue(
      inputParams.duration,
      dropdownsData.durations
    );
  }
  if (inputParams.targetedHotelList) {
    promoSettings.targetedHotelList = inputParams.targetedHotelList;
  }

  if (inputParams.userSessionAlias) {
    promoSettings.alias = inputParams.userSessionAlias;
  }

  if (inputParams.promoStartDate) {
    promoSettings.promoTravelStart = inputParams.promoStartDate;
  }
  if (inputParams.promoEndDate) {
    promoSettings.promoTravelEnd = inputParams.promoEndDate;
  }
  if (inputParams.promotionId) {
    promoSettings.promoId = inputParams.promotionId;
  }

  if (inputParams.promoSearchLang) {
    promoSettings.promoSearchLang = inputParams.promoSearchLang;
  }

  return promoSettings;
};
